import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Alert,
  Box,
  Chip,
  Autocomplete
} from '@mui/material';
import { toast } from 'react-hot-toast';

// Types
interface Account {
  id: number;
  code: string;
  name: string;
  account_type: string;
  parent_id?: number;
  vat_rate: string;
  is_vat_applicable: boolean;
  is_active: boolean;
  full_name: string;
}

interface AddAccountDialogProps {
  open: boolean;
  onClose: () => void;
  onAccountAdded: () => void;
  accounts: Account[];
}

interface AccountFormData {
  code: string;
  name: string;
  account_type: string;
  parent_id: number | null;
  vat_rate: string;
  is_vat_applicable: boolean;
  is_active: boolean;
}

const AddAccountDialog: React.FC<AddAccountDialogProps> = ({
  open,
  onClose,
  onAccountAdded,
  accounts
}) => {
  const [formData, setFormData] = useState<AccountFormData>({
    code: '',
    name: '',
    account_type: '',
    parent_id: null,
    vat_rate: 'standard',
    is_vat_applicable: true,
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setFormData({
        code: '',
        name: '',
        account_type: '',
        parent_id: null,
        vat_rate: 'standard',
        is_vat_applicable: true,
        is_active: true
      });
      setErrors({});
    }
  }, [open]);

  // Account type options
  const accountTypes = [
    { value: 'asset', label: 'Asset', description: 'Resources owned by the business' },
    { value: 'liability', label: 'Liability', description: 'Debts owed by the business' },
    { value: 'equity', label: 'Equity', description: 'Owner\'s stake in the business' },
    { value: 'income', label: 'Income', description: 'Revenue and other income' },
    { value: 'expense', label: 'Expense', description: 'Costs and expenses' }
  ];

  // Sales Tax rate options
  const vatRates = [
    { value: 'standard', label: 'Standard (20%)', percentage: 20 },
    { value: 'reduced', label: 'Reduced (5%)', percentage: 5 },
    { value: 'zero', label: 'Zero (0%)', percentage: 0 },
    { value: 'exempt', label: 'Exempt', percentage: 0 }
  ];

  // Get suggested account code based on type
  const getSuggestedAccountCode = (accountType: string): string => {
    const existingCodes = accounts.map(acc => parseInt(acc.code)).filter(code => !isNaN(code));
    const maxCode = Math.max(...existingCodes, 0);

    switch (accountType) {
      case 'asset':
        // Assets: 1000-1999
        const assetCodes = existingCodes.filter(code => code >= 1000 && code < 2000);
        return assetCodes.length === 0 ? '1000' : String(Math.max(...assetCodes) + 10);
      case 'liability':
        // Liabilities: 2000-2999
        const liabilityCodes = existingCodes.filter(code => code >= 2000 && code < 3000);
        return liabilityCodes.length === 0 ? '2000' : String(Math.max(...liabilityCodes) + 10);
      case 'equity':
        // Equity: 3000-3999
        const equityCodes = existingCodes.filter(code => code >= 3000 && code < 4000);
        return equityCodes.length === 0 ? '3000' : String(Math.max(...equityCodes) + 10);
      case 'income':
        // Income: 4000-4999
        const incomeCodes = existingCodes.filter(code => code >= 4000 && code < 5000);
        return incomeCodes.length === 0 ? '4000' : String(Math.max(...incomeCodes) + 10);
      case 'expense':
        // Expenses: 5000-9999
        const expenseCodes = existingCodes.filter(code => code >= 5000);
        return expenseCodes.length === 0 ? '5000' : String(Math.max(...expenseCodes) + 10);
      default:
        return String(maxCode + 10);
    }
  };

  // Handle form field changes
  const handleChange = (field: keyof AccountFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-suggest account code when type changes
    if (field === 'account_type' && value) {
      const suggestedCode = getSuggestedAccountCode(value);
      setFormData(prev => ({
        ...prev,
        code: suggestedCode
      }));
    }

    // Auto-configure Sales Tax settings based on account type
    if (field === 'account_type') {
      if (value === 'asset' || value === 'liability' || value === 'equity') {
        setFormData(prev => ({
          ...prev,
          vat_rate: 'exempt',
          is_vat_applicable: false
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          vat_rate: 'standard',
          is_vat_applicable: true
        }));
      }
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.code.trim()) {
      newErrors.code = 'Account code is required';
    } else if (!/^\d{3,10}$/.test(formData.code)) {
      newErrors.code = 'Account code must be 3-10 digits';
    } else if (accounts.some(acc => acc.code === formData.code)) {
      newErrors.code = 'Account code already exists';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'Account name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Account name must be less than 255 characters';
    }

    if (!formData.account_type) {
      newErrors.account_type = 'Account type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/v1/accounts/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      toast.success(`Account "${result.full_name}" created successfully!`);
      onAccountAdded();
      onClose();
    } catch (err) {
      console.error('Error creating account:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  // Get parent account options (same type only)
  const parentAccountOptions = accounts.filter(acc =>
    acc.account_type === formData.account_type && acc.is_active
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Add New Account
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create a new account in your chart of accounts
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Account Code */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Account Code"
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value)}
              error={!!errors.code}
              helperText={errors.code || 'Unique 3-10 digit code (e.g., 1100)'}
              placeholder="1100"
            />
          </Grid>

          {/* Account Name */}
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              label="Account Name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || 'Descriptive name for the account'}
              placeholder="Bank Current Account"
            />
          </Grid>

          {/* Account Type */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth error={!!errors.account_type}>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={formData.account_type}
                label="Account Type"
                onChange={(e) => handleChange('account_type', e.target.value)}
              >
                {accountTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box>
                      <Typography variant="body1">{type.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {type.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.account_type && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {errors.account_type}
                </Typography>
              )}
            </FormControl>
          </Grid>

          {/* Parent Account */}
          <Grid item xs={12} md={6}>
            <Autocomplete
              options={parentAccountOptions}
              getOptionLabel={(option) => option.full_name}
              value={parentAccountOptions.find(acc => acc.id === formData.parent_id) || null}
              onChange={(_, value) => handleChange('parent_id', value?.id || null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Parent Account (Optional)"
                  helperText="Select a parent account for hierarchy"
                />
              )}
              disabled={!formData.account_type}
            />
          </Grid>

          {/* Sales Tax Configuration */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth disabled={!formData.is_vat_applicable}>
              <InputLabel>Sales Tax Rate</InputLabel>
              <Select
                value={formData.vat_rate}
                label="Sales Tax Rate"
                onChange={(e) => handleChange('vat_rate', e.target.value)}
              >
                {vatRates.map((rate) => (
                  <MenuItem key={rate.value} value={rate.value}>
                    {rate.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_vat_applicable}
                  onChange={(e) => handleChange('is_vat_applicable', e.target.checked)}
                />
              }
              label="Sales Tax Applicable"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Whether this account is subject to Sales Tax
            </Typography>
          </Grid>

          {/* Account Status */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
              }
              label={formData.is_active ? "Active Account" : "Inactive Account"}
            />
            <Typography variant="caption" color="text.secondary" display="block">
              Inactive accounts are hidden from most views
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAccountDialog;
